import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {t} from 'i18next';
import AurSvgIcon from '../../common/aur-svg-icon/AurSvgIcon';
import {Box, Button, ButtonGroup, Grid, Typography} from '@mui/material';
import AurIcon, {IconColors, IconNames} from '../../common/aur-icon/AurIcon';
import AurDataGrid from '../../common/aur-data-grid/AurDataGrid';
import AurSearchBox from '../../common/aur-search-box/AurSearchBox';
import {getConciergeReportColumns} from '../../../utils/conciergeReportsUtils';
import {GridSortModel} from '@mui/x-data-grid';
import {
  CUSTOM_REPORT_COL_HEIGHT,
  defaultReportFileName,
  failedToLoadStatusCodes,
  MIN_SEARCH_CHAR,
  TABLE_PAGE_SIZE_OPTION,
  TABLE_PER_PAGE_DEFAULT_50_ROW,
} from '../../../constants/constants';
import {ReactComponent as UploadIcon} from '../../../assests/svg/upload_button_icon.svg';
import ReportDetailsPanel from '../../concierge-reports/ConciergeDrawerPanel';
import AurPopup from '../../common/aur-popup/AurPopup';
import {
  deleteConciergeReportsFile,
  downloadConciergeReportFile,
  fetchConciergeReportsList,
} from '../../../api/ReportingManagement';
import {useToast} from '../../../contexts/toastContext/ToastContext';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {
  handleConciergeReportsCall,
  setHandleConciergeReportsCall,
} from '../../../redux/slices/conciergeReportSlice';
import {
  downloadFile,
  getFileExtension,
  getFilenameFromHeaders,
  printFile,
} from '../../../utils/utils';
import AurDataGridErrorOverlay from '../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import {
  NullReportsProps,
  ReportData,
  ReportListProps,
  RowData,
} from '../../../types/concierge-reports/conciergeReportsType';
import {AxiosError} from 'axios';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_DATA_MODE_AUTO,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_REPORT_TYPE,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {AuthorizationContext} from '../../../contexts/authorizationContext/AuthorizationContext';
import {Permissions, Resources} from '../../../utils/authorization';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../hooks/permissions/usePermissionsByPage';

/**
 * NUll State Screen for concierge reports
 * @returns
 */
const NullReports = (props: NullReportsProps) => {
  const {isClient, canUploadConciergeReports} = props;
  const [showDrawer, setShowDrawer] = useState(false);
  const [type, setType] = useState('');

  const clientRole = useSelector(
    (state: RootState) => state.conciergeReport.isClient
  );

  const showUpload = useSelector(
    (state: RootState) => state.conciergeReport.showUpload
  );

  const permissionContext = useContext(AuthorizationContext);

  const userRole = useSelector(
    (state: RootState) => state?.analytics?.user?.role
  );

  const hasSysAdminRole = useMemo(
    () => ['admin', 'client-admin'].includes(userRole ?? ''),
    [userRole]
  );

  const handleClick = () => {
    setType('upload');
    setShowDrawer(true);
  };

  const shouldShowUploadButton = useMemo(
    () =>
      !clientRole &&
      showUpload &&
      ((!isClient &&
        permissionContext.cannot(
          Permissions.Access,
          Resources.Reports_RestrictCustomUpload
        )) ||
        (isClient && canUploadConciergeReports && !hasSysAdminRole)),
    [
      canUploadConciergeReports,
      clientRole,
      hasSysAdminRole,
      isClient,
      permissionContext,
      showUpload,
    ]
  );

  return (
    <>
      <Box className="no-concierge-reports">
        <Box className="details-box">
          <AurSvgIcon src="images/concierge_reports_null.svg" />
          <Box className="details">
            <Typography className="typo-text headline2_size mist-color-100">
              {t('conciergeReports.nullState.title')}
            </Typography>
            {shouldShowUploadButton && (
              <Typography className="typo-text body2_size mist-color-400">
                {t('conciergeReports.nullState.content')}
              </Typography>
            )}
          </Box>
          {/* Button should display based on user role */}
          {shouldShowUploadButton && (
            <Button
              id="basic-button"
              startIcon={<UploadIcon aria-hidden="true" />}
              className="typo-text buttonMedium_size custom-menu-button custom-button"
              variant="contained"
              sx={{
                textTransform: 'none',
              }}
              onClick={handleClick}
            >
              <Typography className="typo-text buttonMedium_size">
                {t('conciergeReports.uploadReport')}
              </Typography>
            </Button>
          )}
        </Box>
      </Box>
      {showDrawer && (
        <ReportDetailsPanel
          setShowDrawer={setShowDrawer}
          showDrawer={showDrawer}
          type={type}
        />
      )}
    </>
  );
};

/**
 * List component for concierge reports
 * @returns
 */

// In development: Need to integrate API and based on develop business logic
const ReportsList = (props: ReportListProps) => {
  const {trackGAEvent} = useAnalytics();

  const {reportList, reportsCount, clientId, projectId, mode, clientRole} =
    props;
  const [rows, setRows] = useState<RowData[]>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState<(number | string)[]>([]);
  const [selectedReports, setSelectedReports] = useState<RowData | undefined>();
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(25);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'updateDate',
      sort: 'desc',
    },
  ]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [type, setType] = useState('');
  const [selectedIdsIndex, setSelectedIdsIndex] = useState<number | undefined>(
    undefined
  );
  const [hasActiveBtnGrp, setHasActiveBtnGrp] = useState<boolean>(false);
  const [deleteContent, setDeleteContent] = useState<JSX.Element | undefined>();
  const [allIds, setAllIds] = useState<string[]>([]);
  const [isRowClicked, setIsRowClicked] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFailedToLoadContent, setIsFailedToLoadContent] =
    useState<boolean>(false);

  const isInitialRender = useRef(true);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState(
    (tableContainerRef.current?.offsetHeight ?? 500) - CUSTOM_REPORT_COL_HEIGHT
  );
  const [disabledPrintBtn, setDisabledPrintBtn] = useState<boolean>(true);
  const [isFetchReportsInitial, setIsFetchReportsInitial] =
    useState<boolean>(true);

  /**
   * To get datagrid height
   */
  useEffect(() => {
    const updateHeight = () => {
      if (tableContainerRef.current) {
        setSize(
          tableContainerRef.current.offsetHeight - CUSTOM_REPORT_COL_HEIGHT
        );
      }
    };
    window.addEventListener('resize', updateHeight);
    updateHeight();

    return () => window.removeEventListener('resize', updateHeight);
  });
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      if (
        isFetchReportsInitial &&
        reportsCount > TABLE_PER_PAGE_DEFAULT_50_ROW
      ) {
        setItemsPerPage(TABLE_PER_PAGE_DEFAULT_50_ROW);
        setIsFetchReportsInitial(false);
      }
      return;
    }
    fetchReportsList();
  }, [currentPage, itemsPerPage, sortModel, searchKeyword]);

  useEffect(() => {
    selectedIds.length ? setHasActiveBtnGrp(true) : setHasActiveBtnGrp(false);
    if (selectedIds.length === 1) {
      const fileName = rows.find((row) => row.id == selectedIds[0])?.name;
      fileName && setDisabledPrintBtn(getFileExtension(fileName) !== 'pdf');
    } else setDisabledPrintBtn(true);
  }, [selectedIds]);

  const fetchReportsList = async () => {
    try {
      setIsLoading(true);
      setIsFailedToLoadContent(false);
      let paramString = '?';

      if (clientId != '' && projectId != '') {
        paramString =
          paramString + `clientId=${clientId}&projectId=${projectId}&`;
      }

      paramString =
        paramString +
        `searchTerm=${searchKeyword}&sortColumn=${sortModel[0]?.field}&sortOrder=${sortModel[0]?.sort}&pageNumber=${currentPage}&pageSize=${itemsPerPage}&mode=${mode}`;

      const result = await fetchConciergeReportsList(paramString);
      getReportsList(result.data?.data || [], result.data?.totalCount || 0);
    } catch (error: unknown) {
      const e = error as AxiosError;
      if (e.status && failedToLoadStatusCodes.includes(e.status))
        setIsFailedToLoadContent(true);
      getReportsList([], 0);
    }
  };

  const handleRowClick = async (data: RowData) => {
    setIsRowClicked(true);
    setSelectedReports(data);
    setType('details');
    setShowDrawer(true);
    trackreportDetails(false);
  };

  const {showToast} = useToast();

  const trackreportDetails = (isBtn: boolean) => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.reports.viewCustomReport'),
        type: isBtn ? GA_CLICK_TYPES.BUTTON : GA_CLICK_TYPES.LINK,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.reports.drawerCustomReportDetails'),
      },
    });
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: {
        subpage_name: t('gaTracking.reports.customReportDetails'),
        subpage_type: GA_SUBPAGE_TYPE.DRAWER,
      },
    });
  };

  /**
   * View report click handler for selected reports
   */
  const viewReportClickHandler = (isBtn: boolean) => {
    handlePanelNavigation(1);
    setType('details');
    setShowDrawer(true);
    trackreportDetails(isBtn);
  };

  /**
   * Handler to navigate reports on detail page
   * @param number
   */
  const handlePanelNavigation = (number: number) => {
    let viewReportId: string | number = '';
    if (isRowClicked) {
      viewReportId = allIds[number - 1];
    } else {
      setSelectedIdsIndex(number);
      viewReportId = selectedIds[number - 1];
    }
    const data: RowData | undefined = rows.find(
      (row) => row.id == viewReportId
    );
    setSelectedReports(data);
  };

  /**
   * Handler for close drawer
   */
  const handleCloseDrawer = () => {
    setIsRowClicked(false);
    setShowDrawer(false);
    setSelectedIdsIndex(undefined);
  };

  const userListColumns = [
    ...getConciergeReportColumns(),
    {
      field: 'arrow',
      headerName: '',
      minWidth: 24,
      flex: 0.4,
      sortable: false,
      headerClassName: 'no-separator',
      isBorderNotRequired: true,
      renderCell: (params: {row: RowData}) => (
        <img
          style={{cursor: 'pointer'}}
          src="/assests/images/chev_right.svg"
          alt="right-chev"
          onClick={() => {
            handleRowClick(params?.row);
          }}
        />
      ),
    },
  ];

  const getReportsList = (reportList: ReportData[], reportsCount: number) => {
    let reportsIds: string[] = [];
    const data = reportList.map((item: ReportData) => {
      reportsIds = [...reportsIds, item.id];
      return {
        id: item.id,
        name: item.fileName,
        client: item.client?.clientName ? item.client.clientName : '',
        clientId: item.client?.clientId ? item.client.clientId : '',
        project: item.project?.projectName ? item.project.projectName : '',
        projectId: item.project?.projectId ? item.project.projectId : '',
        description: item.description ? item.description : '',
        updateUser: item.updateUser,
        updateDate: item.updateDate,
        userList: item.userList,
        fileName: item.fileName,
        projectCode: item.project?.projectCode ? item.project.projectCode : '',
        createdBy: item.createdBy,
      };
    });
    setAllIds(reportsIds);
    setRows(data);
    setTotalRowCount(reportsCount);
    setIsLoading(false);
  };

  useEffect(() => {
    getReportsList(reportList, reportsCount);
  }, []);

  const onSetSortModel = (sortModel: GridSortModel) => {
    setSortModel(sortModel);
  };

  const handleDeleteClick = () => {
    const fileNameList = rows.filter((item) => selectedIds.includes(item.id));
    const content = (
      <div className="custom-filename-list">
        <Typography className={`typo-text body2_size content-text`}>
          {selectedIds.length == 1
            ? t('conciergeReports.deletePopup.content')
            : t('conciergeReports.deletePopup.contentMultiple')}
        </Typography>
        <div>
          {fileNameList.map((report: RowData) => (
            <Typography
              key={report.id}
              className={`typo-text subhead4_size content-text`}
            >
              {report.name}
            </Typography>
          ))}
        </div>
      </div>
    );
    setDeleteContent(content);
    setShowDeleteModal(true);
  };

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false);
  };

  const deleteFile = async () => {
    try {
      const response = await deleteConciergeReportsFile(selectedIds);
      if (response?.data?.meta?.status == 'Success') {
        setShowDeleteModal(false);
        setSelectedIds([]);
        fetchReportsList();
      }
    } catch (e: unknown) {
      const error = e as AxiosError;
      showToast(
        t('conciergeReports.toastMsg.deleteError'),
        'error',
        undefined,
        'center',
        undefined,
        {
          code: error?.code,
        }
      );
    }
  };

  const getSelectedReportId = () =>
    rows.find((row) => row.id == selectedIds[0]);

  const downloadPrintHandler = async (value: string) => {
    try {
      const reportId = getSelectedReportId()?.id;
      if (reportId) {
        const response = await downloadConciergeReportFile(reportId);
        const resHeaders = response.headers;
        const fileName =
          getFilenameFromHeaders(resHeaders['content-disposition']) ||
          defaultReportFileName;
        const resdata = response.data;
        trackGAEvent({
          eventName:
            value === 'print'
              ? GA_EVENT_NAMES.PRINT
              : GA_EVENT_NAMES.FILE_DOWNLOAD,
          file_extension: getFileExtension(fileName),
          click: {
            name: `${t('gaTracking.reports.listView')} ${value}`,
            type: GA_CLICK_TYPES.BUTTON,
            region: GA_REGIONS.BODY,
            action: t(`gaTracking.reports.${value}Report`),
          },
        });

        if (
          value === 'print' &&
          resHeaders['content-type'] === 'application/pdf'
        )
          printFile(resdata);
        else downloadFile(resdata, fileName);
      }
    } catch (error: unknown) {
      const err = error as AxiosError;
      showToast(err.message, 'error', undefined, 'center', undefined, {
        code: err?.code,
      });
    }
  };

  return (
    <>
      <Box>
        <Box className="concierge-reports-list-main-conatiner">
          {/* Search Box Container */}
          <Box className="search-container">
            <Grid container>
              <Grid item xs={2.85}>
                <AurSearchBox
                  label={t('conciergeReports.searchPlaceholder')}
                  minCharForSearch={MIN_SEARCH_CHAR}
                  onSearch={(keyword: string) => {
                    setSearchKeyword(keyword);
                    setSelectedIds([]);
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Button Group Box for actions */}
          <Box className="button-grp-box">
            <ButtonGroup
              variant="contained"
              aria-label={t('buttonGroup.basic')}
              className="button-grp"
            >
              <Button
                disabled={!hasActiveBtnGrp}
                className="inner-btn typo-text buttonSmall_size"
                onClick={() => {
                  viewReportClickHandler(true);
                }}
                startIcon={
                  <AurIcon
                    icon={IconNames.Info}
                    color={IconColors.Custom}
                    size="small"
                    customClassName="icon-custom"
                  />
                }
              >
                {t('conciergeReports.buttonsLabels.viewDetails')}
              </Button>
              <Button
                disabled={disabledPrintBtn}
                onClick={() => downloadPrintHandler('print')}
                className="inner-btn typo-text buttonSmall_size"
                value="print"
                startIcon={
                  <AurIcon
                    icon={IconNames.Print}
                    color={IconColors.Custom}
                    size="small"
                    customClassName="icon-custom"
                  />
                }
              >
                {t('conciergeReports.buttonsLabels.print')}
              </Button>
              <Button
                disabled={selectedIds.length !== 1}
                className="inner-btn typo-text buttonSmall_size"
                onClick={() => downloadPrintHandler('download')}
                value="download"
                startIcon={
                  <AurIcon
                    icon={IconNames.Download}
                    color={IconColors.Custom}
                    size="small"
                    customClassName="icon-custom"
                  />
                }
              >
                {t('conciergeReports.buttonsLabels.download')}
              </Button>
              {!clientRole && (
                <Button
                  disabled={!hasActiveBtnGrp}
                  className="inner-btn typo-text buttonSmall_size"
                  data-testid="delete-report"
                  onClick={handleDeleteClick}
                  startIcon={
                    <AurIcon
                      icon={IconNames.Remove}
                      color={IconColors.Custom}
                      size="small"
                      customClassName="icon-custom"
                    />
                  }
                >
                  {t('conciergeReports.buttonsLabels.delete')}
                </Button>
              )}
            </ButtonGroup>
          </Box>

          {/* Reports Listing */}
          <Box ref={tableContainerRef}>
            <AurDataGrid
              className="concierge-reports-grid"
              rows={rows}
              columns={userListColumns}
              visibleToolTip={true}
              pageSizeOptions={TABLE_PAGE_SIZE_OPTION}
              checkBoxVisible={true}
              entityType={t('common.customReports')}
              onSelectionChange={setSelectedIds}
              selectedIds={selectedIds}
              getRowId={(row) => row.id}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              totalRowCount={totalRowCount}
              sortModel={sortModel}
              setSortModel={onSetSortModel}
              disableRowSelectionOnClick
              handleRowClick={(params: {row: RowData}) =>
                handleRowClick(params?.row)
              }
              initialState={
                clientRole
                  ? {
                      columns: {
                        columnVisibilityModel: {
                          // Hide columns Client & Project, the other columns will remain visible
                          client: false,
                          project: false,
                        },
                      },
                    }
                  : {}
              }
              noRowsOverlay={
                isFailedToLoadContent ? AurDataGridErrorOverlay : undefined
              }
              onRefresh={fetchReportsList}
              containerHeight={size}
              trackPaginationActionName={t(
                'gaTracking.global.reportPagination'
              )}
            />
          </Box>
        </Box>
      </Box>
      {showDrawer && (
        <ReportDetailsPanel
          setShowDrawer={handleCloseDrawer}
          showDrawer={showDrawer}
          selectedReport={selectedReports}
          totalReports={isRowClicked ? allIds.length : selectedIds.length}
          reportNumber={
            isRowClicked
              ? allIds.indexOf(selectedReports?.id ?? '') + 1
              : selectedIdsIndex
          }
          handleDetailsNavigation={handlePanelNavigation}
          type={type}
        />
      )}

      {showDeleteModal && (
        <AurPopup
          className="aur-unsaved-changes-popup"
          open={showDeleteModal}
          onOk={deleteFile}
          onCancel={handleDeleteModalClose}
          onClose={handleDeleteModalClose}
          popupTitle={t('conciergeReports.deletePopup.title')}
          popupHeading={
            selectedIds.length == 1
              ? t('conciergeReports.deletePopup.heading')
              : t('conciergeReports.deletePopup.headingMultiple')
          }
          showPopupHeading={true}
          showPopupTitle={true}
          showCloseIcon={true}
          popupContent={deleteContent}
          labelActionCancel={t('conciergeReports.deletePopup.cancelBtn')}
          labelActionOk={t('conciergeReports.deletePopup.deleteBtn')}
          contentType="element"
          headingIcon={
            <AurIcon
              icon={IconNames.Error}
              color={IconColors.Primary}
              size="large"
            />
          }
        />
      )}
      <AurLoadingIndicator overlay isOpen={isLoading} />
    </>
  );
};

/**
 * Concierge reports content
 * @returns
 */
const ConciergeReportsContent = () => {
  // Need to update logic once API integrate
  const [reportList, setReportList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const searchKeyword = '';
  const currentPage = 1;
  const itemsPerPage = 25;
  const sortModel = [
    {
      field: 'updateDate',
      sort: 'desc',
    },
  ];
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFailedToLoadContent, setIsFailedToLoadContent] =
    useState<boolean>(false);
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();
  const conciergeReportsCall = useSelector(handleConciergeReportsCall);

  const selectedClient = useSelector(
    (state: RootState) => state.selectedClient
  );
  const selectedProject = useSelector(
    (state: RootState) => state.invoiceModal.modalDropDownSelectedItem
  );

  const mode = useSelector((state: RootState) => state.conciergeReport.mode);
  const clientRole = useSelector(
    (state: RootState) => state.conciergeReport.isClient
  );
  const apiResponseTime = useRef('');

  const clientId = clientRole ? selectedClient.clientId : '';
  const projectId = clientRole ? selectedProject.id : '';

  const {canUploadConciergeReports} = usePermissionsByPage(
    PAGE_NAME.REPORTS_VIEW,
    projectId
  );

  useEffect(() => {
    fetchReports();
  }, [clientId, projectId]);

  useEffect(() => {
    if (conciergeReportsCall) {
      fetchReports();
    }
  }, [conciergeReportsCall]);

  const fetchReports = async () => {
    try {
      setIsLoading(true);
      setIsFailedToLoadContent(false);
      let paramString = '?';

      if (clientId != '' && projectId != '') {
        paramString =
          paramString + `clientId=${clientId}&projectId=${projectId}&`;
      }

      paramString =
        paramString +
        `searchTerm=${searchKeyword}&sortColumn=${sortModel[0]?.field}&sortOrder=${sortModel[0]?.sort}&pageNumber=${currentPage}&pageSize=${itemsPerPage}&mode=${mode}`;

      const result = await fetchConciergeReportsList(paramString);
      setReportList(result.data?.data || []);
      setTotalCount(result.data?.totalCount || 0);
      setIsLoading(false);
      dispatch(setHandleConciergeReportsCall(false));
      apiResponseTime.current = result.responseTime;
    } catch (error: unknown) {
      const e = error as AxiosError;
      if (e.status && failedToLoadStatusCodes.includes(e.status))
        setIsFailedToLoadContent(true);
      setReportList([]);
      setTotalCount(0);
      setIsLoading(false);
      dispatch(setHandleConciergeReportsCall(false));
      apiResponseTime.current = (error as any).responseTime;
    } finally {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.PAGE_VIEW,
        page: GA_DATA_MODE_AUTO,
        user: GA_DATA_MODE_AUTO,
        report: {
          name: t('gaTracking.reports.conciergeReports.title'),
          type: GA_REPORT_TYPE.CUSTOM,
          load_time: apiResponseTime.current,
        },
      });
    }
  };

  return (
    <Box className="concierge-reports-content">
      {isLoading ? (
        <Box className="loader-container">
          <AurLoadingIndicator show={isLoading} />
        </Box>
      ) : reportList.length > 0 ? (
        <ReportsList
          reportList={reportList}
          reportsCount={totalCount}
          mode={mode}
          clientRole={clientRole}
          clientId={clientId}
          projectId={projectId}
        />
      ) : isFailedToLoadContent ? (
        <Box className="failed-to-load">
          <AurDataGridErrorOverlay onRefresh={fetchReports} />
        </Box>
      ) : (
        <NullReports
          isClient={clientRole}
          canUploadConciergeReports={canUploadConciergeReports ?? false}
        />
      )}
    </Box>
  );
};

export default ConciergeReportsContent;
